import { Injectable, computed, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  createObservableBroadcastChannel,
  ObservableBroadcastChannel,
} from "src/app/services/aptContext/broadcast-channels";
import invariant from "tiny-invariant";

export interface FavoriteEngagement {
  engagementId: string;
}

type BroadcastMessage = { type: "add" } | { type: "remove" };

@Injectable({
  providedIn: "root",
})
export class FavoriteEngagementWatcherService {
  private bc: ObservableBroadcastChannel<BroadcastMessage>;

  constructor() {
    //
    // We want to keep track of open documents across tabs
    //
    this.bc = createObservableBroadcastChannel<BroadcastMessage>(
      "favorite-engagement-watcher",
    );
    this.bc.obs$.pipe(takeUntilDestroyed()).subscribe((message) => {
      this.pullFavoriteEngagementsFromLocalStorage();
    });
    this.pullFavoriteEngagementsFromLocalStorage();
  }

  public favoriteEngagements = signal<FavoriteEngagement[]>([]);
  public favoriteEngagementIds = computed(() => {
    const favoriteEngagements = this.favoriteEngagements();
    return favoriteEngagements.map((x) => x.engagementId);
  });

  private pushFavoriteEngagementsToLocalStorage(message: BroadcastMessage) {
    const favoriteEngagements = this.favoriteEngagements();
    localStorage.setItem(
      "favoriteEngagements",
      JSON.stringify(favoriteEngagements),
    );

    // Notify other tabs
    this.bc.postMessage(message);
  }

  private pullFavoriteEngagementsFromLocalStorage() {
    const fromStorage = globalThis["localStorage"]
      ? (localStorage.getItem("favoriteEngagements") as
          | string
          | null
          | undefined)
      : undefined;

    const parsedDocuments = fromStorage
      ? (JSON.parse(fromStorage) as FavoriteEngagement[])
      : [];

    // Blow away current state
    this.favoriteEngagements.set(parsedDocuments);
  }

  public addFavoriteEngagement(engagementId: string) {
    invariant(engagementId);

    // Make a copy
    const favoriteEngagements = [...this.favoriteEngagements()];

    const found = favoriteEngagements.find(
      (x) => x.engagementId === engagementId,
    );
    if (found) {
      return;
    }

    favoriteEngagements.push({ engagementId });

    this.favoriteEngagements.set(favoriteEngagements);
    this.pushFavoriteEngagementsToLocalStorage({
      type: "add",
    });
  }

  public removeFavoriteEngagement(engagementId: string) {
    invariant(engagementId);

    // Make a copy
    const favoriteEngagements = [...this.favoriteEngagements()];

    const foundIndex = favoriteEngagements.findIndex(
      (x) => x.engagementId === engagementId,
    );
    if (foundIndex === -1) {
      return;
    }

    favoriteEngagements.splice(foundIndex, 1);

    this.favoriteEngagements.set(favoriteEngagements);
    this.pushFavoriteEngagementsToLocalStorage({
      type: "remove",
    });
  }
}
